import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { doc, updateDoc } from 'firebase/firestore';
import { AiOutlineFullscreen, AiOutlineClose, AiOutlineFullscreenExit } from 'react-icons/ai';
import { FaCheck } from 'react-icons/fa';
import { API } from '@unboared/react';

import { db } from '~/config/firebase.config';
import { useFullscreen } from '~/hooks/useFullscreen';
import { AvatarCarousel } from '~/components/AvatarCarousel';
import { PlayerLanguagePicker } from '~/components/LanguageSelector';
import { CODE_CLIENT_ID } from '~/config/endpoints.config';
import { PrivacyNotice } from '~/components/PrivacyNotice';
import { AvatarCredit } from '~/components/AvatarCredit/AvatarCredit';
import { BrandLogo } from '~/components/branding/BrandLogo';
import { useProfileSettings } from '~/stores/useProfileSettings';

import styles from './GamepadSettings.module.css';

const fieldNames = ['username', 'email', 'age', 'phoneNumber', 'firstname', 'lastname'];

export function GamepadSettings() {
    const { t } = useTranslation();
    const { isFullscreen, isSupported: fullscreenSupported, toggleFullscreen } = useFullscreen();
    const { toggleProfileSettings, informationsSaved } = useProfileSettings();
    const [errorMessage, setErrorMessage] = useState('');
    const [errorType, setErrorType] = useState('');
    const [changed, setChanged] = useState(false);

    const [userData, setUserData] = useLocalStorage<any>('userData', {});
    const [searchParams] = useSearchParams();
    // Langue changée en pleine partie : la console suit (i18n), et le jeu ouvert est
    // prévenu via API → onLanguageChange (useWebViewPlugin). Sur le téléphone, API est
    // le SocketIoApi de la lib, dont `loadLanguage(lang)` reste local (diffusion
    // seulement avec un 2e argument `true`, que le type commun ne déclare pas).
    const onLanguageChanged = (code: string) => API.loadLanguage(code);
    const [clients] = useLocalStorage<string[]>('clients', []);

    const [selectedAvatar, setSelectedAvatar] = useState<string>(userData.avatar);
    const dataNames = Object.keys(userData).filter((key) => key !== 'gdprConsent' && key !== 'uid' && key !== 'avatar');

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        const { uid } = userData;

        setErrorType('');
        let errType = '';
        setErrorMessage('');
        let errorMsg = '';
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const updatedUserData: any = {
            username: formData.get('username') as string,
            uid,
            avatar: selectedAvatar,
        };

        if (updatedUserData.username.length < 3) {
            errorMsg = t('gamepad.settings.error.invalidUsername');
        }

        for (const key of fieldNames) {
            const value = formData.get(key) as string;
            if (value) {
                if (key === 'age') {
                    updatedUserData.age = Number(value);
                    if (!updatedUserData.age) {
                        errorMsg = t('gamepad.settings.error.invalidAge');
                        errType = 'age';
                        break;
                    }
                } else if (key === 'email') {
                    if (!/\S+@\S+\.\S+/.test(value)) {
                        errorMsg = t('gamepad.settings.error.invalidEmail');
                        errType = 'email';
                        break;
                    }
                    updatedUserData.email = value;
                } else {
                    updatedUserData[key] = value;
                }
            } else if (dataNames.includes(key)) {
                errorMsg = `${t('gamepad.settings.error.fill')} ${key}`;
                errType = key;
                break;
            }
        }

        if (errorMsg) {
            setErrorMessage(errorMsg);
            setErrorType(errType);
        } else {
            for (const clientID of clients) {
                const userDocRef = doc(db, 'clients', clientID, 'userData', uid);
                updateDoc(userDocRef, { ...updatedUserData })
                    .then(() => {
                        console.log('User data saved successfully!');
                    })
                    .catch((error) => {
                        console.error('Error saving user data: ', error);
                    });
            }
            API.loadPlayer({ ...updatedUserData, deviceID: API.getDeviceID(), color: API.getColor(API.getDeviceID()) });
            setUserData({ ...updatedUserData });
            setChanged(false);
            informationsSaved();
        }
    };

    const saveDisabled = !changed && selectedAvatar === userData.avatar;

    return (
        <div className={styles.overlay}>
            <header className={styles.header}>
                <span className={styles.brand}>
                    <BrandLogo className={styles.logo} clientID={searchParams.get(CODE_CLIENT_ID)} />
                    <span className={styles.title}>{t('gamepad.settings.title')}</span>
                </span>
                <div className={styles.actions}>
                    {/* Masqué là où il ne ferait rien (iPhone, navigateurs d'applis). */}
                    {fullscreenSupported && (
                        <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={toggleFullscreen}
                            aria-label={t('gamepad.settings.fullscreen')}
                        >
                            {isFullscreen ? <AiOutlineFullscreenExit /> : <AiOutlineFullscreen />}
                        </button>
                    )}
                    <button
                        type="button"
                        className={styles.iconBtn}
                        onClick={toggleProfileSettings}
                        aria-label={t('global.close')}
                    >
                        <AiOutlineClose />
                    </button>
                </div>
            </header>

            <form className={styles.form} onSubmit={handleSubmit}>
                <PlayerLanguagePicker
                    clientID={searchParams.get(CODE_CLIENT_ID)}
                    screen="inGame"
                    onChanged={onLanguageChanged}
                />
                <AvatarCarousel initialAvatar={userData.avatar} onChange={setSelectedAvatar} />

                {fieldNames.map((key) => (
                    <div className={styles.field} key={key}>
                        <label className={styles.label} htmlFor={key}>
                            {t(`gamepad.settings.${key}`)}
                        </label>
                        <input
                            id={key}
                            name={key}
                            className={`${styles.input} ${errorType === key ? styles.inputError : ''}`}
                            defaultValue={userData[key]}
                            onChange={() => setChanged(true)}
                            placeholder={`${t('gamepad.settings.your')} ${t(`gamepad.settings.${key}`).toLowerCase()}`}
                        />
                    </div>
                ))}

                {errorMessage && <p className={styles.error}>{errorMessage}</p>}

                <button type="submit" className={styles.cta} disabled={saveDisabled}>
                    <FaCheck />
                    {t('gamepad.settings.save')}
                </button>

                {/* Un seul bloc : l'espacement du formulaire ne s'applique qu'une fois aux deux mentions. */}
                <div>
                    <PrivacyNotice className={styles.privacy} />
                    <AvatarCredit className={styles.privacy} />
                </div>
            </form>
        </div>
    );
}
